import { Link, createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { HelperError } from '../api/helper';
import {
  useDeleteDirectory,
  useDirectories,
  useRegisterDirectory,
  useReindexDirectory,
} from '../api/helper-queries';
import { useHelperStore } from '../stores/helper';
import { rootRoute } from './__root';

export const directoriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/directories',
  component: DirectoriesPage,
});

const PERMISSIONS = ['read', 'reference', 'write', 'none'] as const;
const TRUST_ORIGINS = ['user-authored', 'downloaded', 'shared-by-third-party'] as const;

function DirectoriesPage() {
  const credentials = useHelperStore((s) => s.credentials);
  const directories = useDirectories();
  const register = useRegisterDirectory();
  const remove = useDeleteDirectory();
  const reindex = useReindexDirectory();

  const [path, setPath] = useState('');
  const [permission, setPermission] = useState<(typeof PERMISSIONS)[number]>('read');
  const [trustOrigin, setTrustOrigin] = useState<(typeof TRUST_ORIGINS)[number]>('user-authored');

  if (!credentials) {
    return (
      <section>
        <h1 style={{ marginTop: 0 }}>Directories</h1>
        <p>
          Pair the helper first. <Link to="/pair">Go to pairing →</Link>
        </p>
      </section>
    );
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!path.trim()) return;
    register.mutate(
      { path: path.trim(), permission, trust_origin: trustOrigin },
      {
        onSuccess: () => setPath(''),
      },
    );
  };

  return (
    <section style={{ maxWidth: 800 }}>
      <h1 style={{ marginTop: 0 }}>Directories</h1>
      <p style={{ color: '#666' }}>
        Choose folders the helper should index. Files are processed entirely on this machine;
        nothing leaves your device unless you explicitly share it during a meeting.
      </p>

      <form
        onSubmit={onSubmit}
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto auto auto',
          gap: 8,
          alignItems: 'end',
          marginBottom: 16,
        }}
      >
        <label>
          <div>Path</div>
          <input
            type="text"
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder="/Users/me/Documents/notes"
            style={{ width: '100%' }}
            required
          />
        </label>
        <label>
          <div>Permission</div>
          <select
            value={permission}
            onChange={(e) => setPermission(e.target.value as (typeof PERMISSIONS)[number])}
          >
            {PERMISSIONS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <label>
          <div>Trust</div>
          <select
            value={trustOrigin}
            onChange={(e) => setTrustOrigin(e.target.value as (typeof TRUST_ORIGINS)[number])}
          >
            {TRUST_ORIGINS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" disabled={register.isPending || !path.trim()}>
          {register.isPending ? 'Adding…' : 'Add directory'}
        </button>
      </form>
      {register.error && <p style={{ color: 'crimson' }}>{describeError(register.error)}</p>}

      {directories.isLoading && <p>Loading directories…</p>}
      {directories.error && <p style={{ color: 'crimson' }}>{describeError(directories.error)}</p>}
      {directories.data && directories.data.directories.length === 0 && (
        <p style={{ color: '#666' }}>No directories yet.</p>
      )}
      {directories.data && directories.data.directories.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e5e5' }}>
              <th style={{ padding: '8px 4px' }}>Path</th>
              <th style={{ padding: '8px 4px' }}>Permission</th>
              <th style={{ padding: '8px 4px' }}>Status</th>
              <th style={{ padding: '8px 4px' }}>Last indexed</th>
              <th style={{ padding: '8px 4px' }} />
            </tr>
          </thead>
          <tbody>
            {directories.data.directories.map((d) => (
              <tr key={d.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '8px 4px' }}>
                  <code>{d.path}</code>
                </td>
                <td style={{ padding: '8px 4px' }}>{d.permission}</td>
                <td style={{ padding: '8px 4px' }}>{d.status}</td>
                <td style={{ padding: '8px 4px' }}>
                  {d.last_indexed_at ? new Date(d.last_indexed_at).toLocaleString() : '—'}
                </td>
                <td style={{ padding: '8px 4px', display: 'flex', gap: 4 }}>
                  <button
                    type="button"
                    onClick={() => reindex.mutate(d.id)}
                    disabled={reindex.isPending}
                  >
                    Re-index
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm(`Remove ${d.path} from indexing?`)) remove.mutate(d.id);
                    }}
                    disabled={remove.isPending}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function describeError(e: unknown): string {
  if (e instanceof HelperError) {
    return `${e.problem?.title ?? 'helper error'} (${e.status})${e.problem?.detail ? `: ${e.problem.detail}` : ''}`;
  }
  if (e instanceof Error) return e.message;
  return String(e);
}
